import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import * as fromActions from './slice';
import { selectStatusPerInstrument } from '~/modules/instrument/slice';
import { catchError, concatMap, filter, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { InstrumentService } from '~/modules/instrument/instrument.service';
import { LoadingStatus, wizePricePeriods } from '@qvs/core';
import { select, Store } from '@ngrx/store';

@Injectable()
export class InstrumentEffects {
  loadInstruments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadInstrumentList),
      concatMap(() =>
        this.instrumentService.loadInstruments().pipe(
          switchMap((instruments) =>
            of(fromActions.upsertInstruments({ entities: instruments }), fromActions.loadInstrumentListSuccess())
          ),
          catchError(() => of(fromActions.loadInstrumentListFailure()))
        )
      )
    )
  );

  loadInstrumentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getInstrumentDetails),
      concatLatestFrom(() => this.store.pipe(select(selectStatusPerInstrument))),
      filter(
        ([{ instrumentId }, statusMap]) =>
          !statusMap.has(instrumentId) ||
          LoadingStatus.FAILED === statusMap.get(instrumentId).details ||
          LoadingStatus.IDLE === statusMap.get(instrumentId).details
      ),
      tap(([{ instrumentId }]) => this.store.dispatch(fromActions.loadInstrumentDetails({ instrumentId }))),
      concatMap(([{ instrumentId }]) =>
        this.instrumentService.loadInstrumentDetails(Number(instrumentId)).pipe(
          switchMap((instrument) =>
            of(fromActions.upsertInstrument({ entity: instrument }), fromActions.loadInstrumentDetailsSuccess({ instrumentId }))
          ),
          catchError(() => of(fromActions.loadInstrumentDetailsFailure({ instrumentId })))
        )
      )
    )
  );

  loadInstrumentPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPrices),
      concatMap(({ instrumentId, period }) =>
        this.instrumentService.loadInstrumentPrices(Number(instrumentId), period).pipe(
          switchMap((prices) => of(fromActions.loadPricesSuccess({ instrumentId, period, priceSeries: prices }))),
          catchError(() => of(fromActions.loadPricesFailure({ period })))
        )
      )
    )
  );

  loadCurrentInstrumentDetailsAndPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setCurrentInstrumentId),
      concatMap(({ instrumentId }) =>
        of(
          fromActions.getInstrumentDetails({ instrumentId }),
          ...wizePricePeriods.map((period) => fromActions.loadPrices({ instrumentId, period }))
        )
      )
    )
  );

  loadWatchlist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadWatchlist),
      concatMap(() =>
        this.instrumentService.loadWatchlist().pipe(
          switchMap((instruments) =>
            of(
              fromActions.upsertInstruments({ entities: instruments }),
              fromActions.loadWatchlistSuccess({ currentlyWatchedIds: instruments.map((instrument) => instrument.id) })
            )
          ),
          catchError(() => of(fromActions.loadWatchlistFailure()))
        )
      )
    )
  );

  addInstrumentToWatchlist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.addToWatchlist),
      concatMap(({ instrumentId }) =>
        this.instrumentService.addToWatchlist(Number(instrumentId)).pipe(
          switchMap(({ watchlistId }) => of(fromActions.addToWatchlistSuccess({ instrumentId, watchlistId }))),
          catchError(() => of(fromActions.addToWatchlistFailure()))
        )
      )
    )
  );

  removeInstrumentFromWatchlist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.removeFromWatchlist),
      concatMap(({ instrumentId, watchlistId }) =>
        this.instrumentService.removeFromWatchlist(Number(watchlistId)).pipe(
          switchMap(() => of(fromActions.removeFromWatchlistSuccess({ instrumentId }))),
          catchError(() => of(fromActions.removeFromWatchlistFailure()))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly instrumentService: InstrumentService
  ) {}
}
